import queryString from 'query-string'
import * as clipboard from 'clipboard-polyfill/text'
import { camelCase, fromPairs, mapKeys } from 'lodash'
import { Dictionary, ReleaseFamily, VersionDescriptor } from '@feedyou-ai/feedbot-schema'
import { isFeedieEmail, isNotEmptyElement } from '@feedyou/utils'

import { apiRequest } from '../../../lib/api'
import { getApiUrl } from '../../../apiService'
import { UserAuthInfo } from '../../../store/user/types'
import { FeatureValues } from '../../../store/features/types'
import { getUrlFeatures as getBetaFeatures } from '../../../store/features/featuresService.url'
import { renderFeatureHierarchy, renderFeatures } from './utils/renderFeatureHierarchy'
import { FeatureValuesHierarchy } from '../../../store/features/selectors'
import { HostingMetadata } from '../../../features/serverBots/types'

export function getDesignerBuildInfo(): Dictionary<string, string> {
  return JSON.parse(import.meta.env.REACT_APP_DESIGNER_BUILD_INFO || '{}')
}

export async function renderDiagnostics(
  user: UserAuthInfo,
  features: FeatureValues,
  featureValueHierarchy: FeatureValuesHierarchy,
  hostingMetadata?: HostingMetadata['buildInfo'],
  deployPermissionUsers: string[] = []
): Promise<string> {
  const betaFeatures = getBetaFeatures(features)
  const isAdmin = isFeedieEmail(user?.email)

  const { sdkFeatures = [], deployedVersion } = hostingMetadata ?? {}

  const urlRow = renderUrlRow()
  const betaFeaturesUrlRow = renderBetaFeaturesUrlRow(betaFeatures, features)
  const dateTimeRow = renderDateTimeRow()
  const userRow = renderUserInfoRow()
  const designerRow = renderDesignerBuildRow(isAdmin)
  const serverRow = await renderServerBuildRow(isAdmin)

  const hostingRow = deployedVersion ? renderHostingBuildRow(isAdmin, deployedVersion) : undefined

  const hostingFeaturesRow = renderRow(
    'Hosting features',
    renderFeatures(fromPairs(sdkFeatures.map(f => [f, 'true'])), {})
  )

  const featureValueRows = renderFeatureHierarchy(featureValueHierarchy)

  const deployPermissionUsersRow = deployPermissionUsers.length
    ? renderRow('Users for deploy bot ', deployPermissionUsers.join('<br/>'))
    : undefined

  const rows = [
    urlRow,
    betaFeaturesUrlRow,
    userRow,
    dateTimeRow,
    designerRow,
    serverRow,
    hostingRow,
    hostingFeaturesRow,
    featureValueRows,
    deployPermissionUsersRow
  ]

  return `
    <table>
      ${rows.filter(isNotEmptyElement).join('')}
    </table>
  `
}

export function renderRow(title: string, value: string, link?: string): string {
  const tag = link ? `<a href="${link}">${value}</a>` : value

  return `
    <tr>
      <th style="min-width: 170px; text-align: right; padding-left: 10px; padding-right: 20px; padding-top: 10px; ">
      <strong>${title}</strong>&emsp; </th>
      <td style="font-family: Courier New; word-break: break-all; ">${tag}</td>
    </tr>
  `
}

function renderUrlRow(): string {
  const title = 'Current URL'
  const value = window.location.href

  return renderRow(title, `<a href="${value}">${value}</a>`)
}

function renderBetaFeaturesUrlRow(betaFeatures: FeatureValues, features: FeatureValues): string {
  if (!betaFeatures || Object.keys(betaFeatures).length === 0) {
    return ''
  }

  const { tariff, ...restFeatures } = mapKeys(features, (v, k) => camelCase(k))
  const query = 'tariff=' + tariff + '&' + queryString.stringify(restFeatures)
  const url = window.location.href + '?' + query

  return renderRow('Beta features URL', `<a href="${url}">${url}</a>`)
}

function renderDesignerBuildRow(isAdmin: boolean): string {
  const { commit, version } = getDesignerBuildInfo()
  return renderBuildInfo('Designer build', isAdmin, commit, version)
}

async function renderServerBuildRow(isAdmin: boolean): Promise<string> {
  const { commit, version } = JSON.parse(
    (await apiRequest(`${getApiUrl()}/build-info`, { checkOk: true })) || {}
  )

  return renderBuildInfo('Server build', isAdmin, commit, version)
}

function renderHostingBuildRow(
  isAdmin: boolean,
  { version, commit, releaseFamily }: VersionDescriptor<'withCommit'>
): string {
  const major =
    releaseFamily === ReleaseFamily.Release ? version.split('.').slice(0, 2).join('.') : null
  const link =
    isAdmin && major ? `https://feedyou.blob.core.windows.net/changelog/v${major}.html` : ''

  return renderRow('Hosting build', `${releaseFamily}/${version} (commit ${commit})`, link)
}

function renderBuildInfo(
  label: string,
  isAdmin: boolean,
  commit?: string,
  fullVersion?: string
): string {
  const version = (fullVersion || '').replace('refs/heads/', '').replace('refs/tags/', '')
  const subCommit = commit?.substr(0, 8)
  const info = `${version || '?'}-${subCommit || '?'}`.replace('?-?', '?')

  if (!label) {
    return info
  }

  const major = version?.match(/(v\d+\.\d+)\.\d+/)?.[1]
  const link = isAdmin && major ? 'https://feedyou.blob.core.windows.net/changelog/latest.html' : ''

  const title = label
  const value = info

  return renderRow(title, value, link)
}

function renderUserInfoRow(): string {
  //@ts-ignore
  const { email, id } = window.Store.getState().user || {}
  return renderRow('User email & ID', [email, id].filter(i => !!i).join(', ') || '?')
}

function renderDateTimeRow(): string {
  const title = 'Date & time'
  const dateAndTime = new Date().toLocaleString('en')

  return renderRow(title, dateAndTime)
}

export function copy(text: string) {
  const textReplaced = text
    .replace(/<\/tr>/g, '\n')
    .replace(/<th[^>]+>/g, '\n')
    .replace(/<br\/>/g, '\n')
    .replace(/<\/?strong>/g, '**')
    .replace(/<\/?[^>]+(>|$)/g, '')
    .replaceAll('&emsp;', '')
    .replaceAll('&nbsp;', ' ')
    .split('\n')
    .map(s => s.trim())
    .join('\n')
    .replace(/\n{2,}/gm, '\n\n')
    .trim()

  clipboard
    .writeText(textReplaced)
    .catch(err => console.warn('Cannot copy diagnostics info to the clipboard', err))
}
